/** Starter content offered during onboarding and used by the built-in advisor. */

export const AVATARS = [
  "🦊", "🐼", "🦁", "🐯", "🐸", "🐙", "🦄", "🐲", "🐵", "🐨", "🐰", "🐶",
  "🐱", "🦖", "🐢", "🦉", "🐝", "🦋", "🚀", "⚽", "🎸", "🎨", "🌟", "🍕",
];

export const PARENT_AVATARS = ["🧑", "👩", "👨", "🧔", "👵", "👴", "🦸", "🧙", "🌻", "☕"];

export const COLORS = ["teal", "violet", "rose", "amber", "sky", "lime", "orange", "fuchsia"] as const;
export type KidColor = (typeof COLORS)[number];

export const CURRENCY_PRESETS = [
  { name: "Coins", icon: "🪙" },
  { name: "Stars", icon: "⭐" },
  { name: "Points", icon: "💎" },
  { name: "Bucks", icon: "💵" },
  { name: "Gems", icon: "💠" },
  { name: "Tokens", icon: "🎟️" },
];

export const ICON_CHOICES = [
  "✅", "🛏️", "🪥", "🧹", "🧺", "🍽️", "🐕", "🐈", "📚", "✏️", "🎒", "🗑️", "🌱", "🚗", "🍂", "❄️",
  "🧽", "🪟", "👕", "🧸", "🛒", "🍳", "🎹", "🏃", "🧘", "💤", "🚿", "🦷", "📖", "🧮", "🧰", "🪴",
  "🎮", "📺", "💵", "🎁", "🍦", "🎬", "🎳", "🏊", "🧩", "🎨", "⚽", "🛹", "📱", "🎧", "🍕", "⭐",
];

export interface ResponsibilityTemplate {
  title: string;
  icon: string;
  frequency: "daily" | "weekly";
  daysOfWeek?: number;
  dueTime?: string;
}

export const RESPONSIBILITY_TEMPLATES: ResponsibilityTemplate[] = [
  { title: "Make your bed", icon: "🛏️", frequency: "daily" },
  { title: "Brush teeth (morning & night)", icon: "🪥", frequency: "daily" },
  { title: "Homework done", icon: "📚", frequency: "daily", daysOfWeek: 0b0111110, dueTime: "18:00" },
  { title: "Clear your dishes", icon: "🍽️", frequency: "daily" },
  { title: "Tidy your room", icon: "🧸", frequency: "daily" },
  { title: "Put laundry in the hamper", icon: "🧺", frequency: "daily" },
  { title: "Feed the pet", icon: "🐕", frequency: "daily" },
  { title: "Pack your school bag", icon: "🎒", frequency: "daily", daysOfWeek: 0b0111110 },
  { title: "Read for 20 minutes", icon: "📖", frequency: "daily" },
  { title: "Practice your instrument", icon: "🎹", frequency: "daily" },
  { title: "Clean your room (deep clean)", icon: "🧹", frequency: "weekly" },
];

export interface JobTemplate {
  title: string;
  icon: string;
  minutes: number;
  effort: 1 | 2 | 3; // light, moderate, hard
  repeat: "once" | "daily" | "weekly" | "anytime";
  minAge?: number;
}

export const JOB_TEMPLATES: JobTemplate[] = [
  { title: "Wash the car", icon: "🚗", minutes: 45, effort: 3, repeat: "weekly", minAge: 8 },
  { title: "Vacuum the living room", icon: "🧹", minutes: 20, effort: 2, repeat: "weekly", minAge: 7 },
  { title: "Rake leaves", icon: "🍂", minutes: 60, effort: 3, repeat: "anytime", minAge: 7 },
  { title: "Fold a load of laundry", icon: "👕", minutes: 20, effort: 1, repeat: "anytime", minAge: 6 },
  { title: "Unload the dishwasher", icon: "🍽️", minutes: 10, effort: 1, repeat: "daily", minAge: 5 },
  { title: "Take out trash & recycling", icon: "🗑️", minutes: 10, effort: 1, repeat: "weekly", minAge: 6 },
  { title: "Weed the garden", icon: "🌱", minutes: 30, effort: 2, repeat: "weekly", minAge: 7 },
  { title: "Clean the bathroom sink & mirror", icon: "🧽", minutes: 15, effort: 2, repeat: "weekly", minAge: 8 },
  { title: "Wash windows", icon: "🪟", minutes: 30, effort: 2, repeat: "anytime", minAge: 9 },
  { title: "Help cook dinner", icon: "🍳", minutes: 40, effort: 2, repeat: "anytime", minAge: 8 },
  { title: "Shovel the driveway", icon: "❄️", minutes: 45, effort: 3, repeat: "anytime", minAge: 10 },
  { title: "Organize the garage shelf", icon: "🧰", minutes: 60, effort: 2, repeat: "once", minAge: 9 },
  { title: "Read a book aloud to a sibling", icon: "📖", minutes: 20, effort: 1, repeat: "anytime", minAge: 7 },
  { title: "Walk the dog", icon: "🐕", minutes: 20, effort: 1, repeat: "daily", minAge: 8 },
];

export const PENALTY_REASONS = [
  "Not following instructions",
  "Unkind words or behavior",
  "Broke a screen-time rule",
  "Not telling the truth",
  "Ignored bedtime",
  "Fighting with a sibling",
];
